import React, { ReactNode, useMemo, useState } from 'react';
import Container from '@mui/material/Container';
import { Box, Button, ButtonGroup, Grid, GridProps, IconButton, SxProps, Typography } from '@mui/material';
import ListHeader from '../components/ListHeader';
import FilterSidebar from '../components/common/filters/FilterSidebar';
import { useIsMobile } from '../hooks/useIsMobile';
import { headerTypographyProps, listPageLayoutStyles, listPageTableWrapperStyles } from '../styles/sxStyling';
import { UserConfigurableModule } from '../types/User';
import { FilterState } from '../components/common/filters/interfaces';
import { PREFERENCES_VALID_KEYS } from '../types/Preference';
import { useGetAllPreferences } from '../services/react-query/preferences';
import { EmployeesScheduleFilters } from '../utility/employee';
import { DateTime } from 'luxon';
import { useGetScheduledEmployeeEvents } from '../services/react-query/employees';
import _, { Dictionary } from 'lodash';
import { humanizeDate } from '../utility/dateUtils';
import DrawerLink from '../components/common/drawers/ModuleDrawer/DrawerLink';
import { Event } from '../types/Event';
import { EventTile } from '../components/dashboard/EventsPanel/EventTile';
import { ExpandLess, ExpandMore } from '@mui/icons-material';

interface IEventData {
  event: Event;
  startCol: number;
  endCol: number;
}

interface IRowData {
  employeeUuid: string;
  employeeLabel: string;
  events: IEventData[];
}

const contentContainerProps: GridProps = { container: true, size: 7, columns: 7, sx: { py: 1 } };
const headerSx: SxProps = { textAlign: 'center', borderBottom: '2px solid', '.MuiTypography-root': { fontWeight: 'bold' } };

// column bounded between Mon-Sun
const boundedCol = (col: number) => Math.min(8, Math.max(col, 1));

// the starting col is where the event actually starts from (inclusive)
// the ending col is where the event ends (exclusive)
// if the event goes into a given day even for 1 second that day gets included
const calcEventCols = (weekStart: DateTime, event: Event): { startCol: number; endCol: number } => {
  const [eventStart, eventEnd] = _.map([event.start_at, event.end_at], d => DateTime.fromISO(d));
  const [startDayDiff, endDayDiff] = _.map([eventStart, eventEnd], d => d.diff(weekStart, ['hours']).hours / 24);

  return {
    startCol: boundedCol(1 + Math.floor(startDayDiff)),
    endCol: boundedCol(1 + Math.ceil(endDayDiff)),
  };
};

const overlappingEvents = (events: IEventData[]): boolean => _.some(_.range(1, 8), col => _.filter(events, event => event.startCol <= col && col < event.endCol).length > 1);

const DatesHeader: React.FC<{ weekStart: DateTime }> = ({ weekStart }) => (
  <>
    <Grid size={1} sx={headerSx}>
      <Typography>Employees</Typography>
    </Grid>
    <Grid {...contentContainerProps} sx={headerSx}>
      <Grid size={7}><Typography>Events</Typography></Grid>
      {
        _.map(_.range(0, 7), dayDiff => {
          const date = weekStart.plus({ day: dayDiff });
          return (
            <Grid size={1}>
              <Typography title={humanizeDate(date.toISO())}>{`${date.weekdayShort}, ${date.day}`}</Typography>
            </Grid>
          );
        })
      }
    </Grid>
  </>
);

const EmployeesSchedule: React.FC = () => {
  const { isMobile } = useIsMobile();
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const [filterState, setFilterState] = useState<FilterState>(undefined);
  const [weekStart, setWeekStart] = useState<DateTime<true>>(DateTime.now().startOf('week'));
  const [multiRow, setMultiRow] = useState<Dictionary<boolean>>({});
  const weekEnd = useMemo(() => weekStart.plus({ days: 6 }), [weekStart]);

  const { data: preferenceData } = useGetAllPreferences({
    params: { filters: { keys: [PREFERENCES_VALID_KEYS.EMPLOYEE_ROLE, PREFERENCES_VALID_KEYS.EMPLOYEE_TITLE] } },
  });

  const { data: employeeEvents } = useGetScheduledEmployeeEvents({
    params: {
      date_range: [weekStart.toISO(), weekEnd.toISO()],
      filters: filterState
    }, enabled: true
  });

  const rowData: IRowData[] = useMemo(() => {
    if (!employeeEvents) return [];

    const eventsByEmployeeUuid = _.groupBy(employeeEvents.event_employees, 'employee_uuid');

    return _.map(employeeEvents.employees, employee => ({
      employeeUuid: employee.uuid,
      employeeLabel: employee.display_name,
      events: _(eventsByEmployeeUuid[employee.uuid]).
        map(employeeEvent => ({ event: employeeEvent.event, ...calcEventCols(weekStart, employeeEvent.event) })).
        sortBy('startCol').
        value(),
    }));
  }, [employeeEvents]);

  const toggleMultiRow = (uuid: string) => setMultiRow({ ...multiRow, [uuid]: !multiRow[uuid] });

  if (isMobile) {
    return (
      <Box sx={{ alignContent: 'center', height: '100%' }}>
        <Typography {...headerTypographyProps} whiteSpace="wrap" textAlign="center">
          We&apos;re not able to correctly show this page at this size. <br /><br /> Please use a device with a larger screen.
        </Typography>
      </Box>
    );
  }

  const renderEvents = (events: IEventData[], multi: boolean) => {
    const children: ReactNode[] = [];
    let remainingEvents = _.cloneDeep(_.uniq(events));
    let col = 1;
    while (!_.isEmpty(remainingEvents)) {
      const eventData = _.find(remainingEvents, { startCol: col });
      if (_.isNil(eventData)) {
        children.push(<Grid size={1} />);
        col++;
      } else {
        const size = eventData.endCol - eventData.startCol;
        children.push(
          <Grid size={size} key={eventData.event.uuid}>
            <EventTile {...eventData.event} />
          </Grid>
        );
        remainingEvents = _.reject(remainingEvents, { event: { uuid: eventData.event.uuid } });
        col += size;
      }

      if (col > 7) {
        if (multi) {
          col = 1;
        } else {
          break;
        }
      }
    }

    return children;
  };

  const renderRow = (item: IRowData) => {
    return (
      <Grid container columns={8} size={8} sx={{ '& > .MuiGrid-root': { borderBottom: '1px solid', pb: 1 } }}>
        <Grid size={1} sx={{ borderRight: '1px solid' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
            <DrawerLink module={UserConfigurableModule.EMPLOYEES} uuid={item.employeeUuid}>{item.employeeLabel}</DrawerLink>
            <IconButton disabled={!overlappingEvents(item.events)} onClick={() => toggleMultiRow(item.employeeUuid)} sx={{ width: 'fit-content', mt: 1 }}>
              {
                multiRow[item.employeeUuid] ?
                  <ExpandLess /> :
                  <ExpandMore />
              }
            </IconButton>
          </Box>
        </Grid>
        <Grid {...contentContainerProps} spacing={0.5} key={item.employeeUuid}>
          {renderEvents(item.events, Boolean(multiRow[item.employeeUuid]))}
        </Grid>
      </Grid>
    );
  };

  return (
    <Container maxWidth="xl">
      <ListHeader
        title="Employees Schedule"
        filtersOpen={sidebarOpen}
        onToggleFilters={() => setSidebarOpen((o) => !o)}
        rightActions={null}
      />

      <Box sx={listPageLayoutStyles}>
        {sidebarOpen && (
          <FilterSidebar
            moduleFilters={EmployeesScheduleFilters(preferenceData ?? [])}
            module={UserConfigurableModule.EMPLOYEES_SCHEDULE} // Adjust if needed
            open={sidebarOpen}
            onClose={() => setSidebarOpen(false)}
            showSearch={false}
            activeFilter={filterState}
            setActiveFilter={setFilterState}
          />
        )}

        <Box sx={listPageTableWrapperStyles}>
          <Box sx={{ display: 'flex', mb: 2, alignItems: 'center' }}>
            <ButtonGroup>
              <Button onClick={() => setWeekStart(weekStart.minus({ weeks: 1 }))}>Prev</Button>
              <Button onClick={() => setWeekStart(DateTime.now().startOf('week'))}>Today</Button>
              <Button onClick={() => setWeekStart(weekStart.plus({ weeks: 1 }))}>Next</Button>
            </ButtonGroup>
            <Typography variant='h5' sx={{ width: '100%', textAlign: 'center' }}>
              {`${humanizeDate(weekStart.toISO())} - ${humanizeDate(weekEnd.toISO())} `}
            </Typography>
          </Box>
          <Grid container columns={8}>
            <DatesHeader weekStart={weekStart} />
            {
              _.map(rowData, item => renderRow(item))
            }
          </Grid>
        </Box>
      </Box>
    </Container>
  );
};

export default EmployeesSchedule;
